// Microphone input — Tone.UserMedia → Tone.Recorder for AudioLayer capture,
// with an optional monitor path (default off; headphones recommended to avoid
// feedback). docs/04 §4.6, docs/05 §5.5.

import * as Tone from "tone";

/** Minimal interface the Looper needs to capture an audio layer. */
export interface AudioRecorder {
  startRecording(): void;
  stopRecording(): Promise<Blob>;
}

export class MicDeniedError extends Error {}

export class MicInput implements AudioRecorder {
  private mic: Tone.UserMedia | null = null;
  private recorder: Tone.Recorder | null = null;
  private monitor: Tone.Gain | null = null;

  get isOpen(): boolean {
    return this.mic?.state === "started";
  }

  async open(): Promise<void> {
    if (this.isOpen) return;
    this.mic = new Tone.UserMedia();
    try {
      await this.mic.open();
    } catch (err) {
      throw new MicDeniedError((err as Error)?.message ?? "Microphone unavailable");
    }
    this.recorder = new Tone.Recorder();
    this.monitor = new Tone.Gain(0).toDestination(); // monitor off by default
    this.mic.connect(this.recorder);
    this.mic.connect(this.monitor);
  }

  setMonitor(on: boolean, gainDb = -6): void {
    this.monitor?.gain.rampTo(on ? Tone.dbToGain(gainDb) : 0, 0.05);
  }

  startRecording(): void {
    void this.recorder?.start();
  }

  async stopRecording(): Promise<Blob> {
    const blob = await this.recorder?.stop();
    return blob ?? new Blob();
  }

  close(): void {
    this.recorder?.dispose();
    this.monitor?.dispose();
    this.mic?.close();
    this.mic?.dispose();
    this.mic = null;
    this.recorder = null;
    this.monitor = null;
  }
}
